import { createSelector } from '@reduxjs/toolkit'
import { getCollectionOrEmptyObject } from '@opallabs/lambda'
import { testFeature } from '@opallabs/principles'
import { Content, Moment, Phase, Response, Service } from '../../../../types'
import { getContent } from '../../getters/entities/content'
import { getMoments } from '../../getters/entities/moments'
import { getPhases } from '../../getters/entities/phases'
import { getResponses } from '../../getters/entities/responses'
import { getServices } from '../../getters/entities/services'
import { normalizeAssignmentsToApprovals } from '../normalizeAssignmentsToApprovals'
import { normalizePhaseItemsToApprovals } from '../normalizePhaseItemsToApprovals'
import {
  selectAssignments,
  selectAssignmentContextMap,
  selectStages,
} from './assignments'
import { selectPhaseItems } from './phaseItems'

const selectMoments = createSelector([getMoments], (moments) =>
  getCollectionOrEmptyObject<Moment>(moments)
)

const selectContents = createSelector([getContent], (contents) =>
  getCollectionOrEmptyObject<Content>(contents)
)

const selectPhases = createSelector([getPhases], (phases) =>
  getCollectionOrEmptyObject<Phase>(phases)
)

const selectServices = createSelector([getServices], (services) =>
  getCollectionOrEmptyObject<Service>(services)
)

const selectResponses = createSelector([getResponses], (responses) =>
  getCollectionOrEmptyObject<Response>(responses)
)

export const selectNormalizedAssignments = createSelector(
  [
    selectAssignments,
    selectStages,
    selectResponses,
    selectAssignmentContextMap,
    selectMoments,
  ],
  (assignments, stages, responses, assignmentsContextMap, moments) =>
    normalizeAssignmentsToApprovals({
      assignments,
      assignmentsContextMap,
      moments,
      responses,
      stages,
    })
)

export const selectNormalizedPhaseItems = createSelector(
  [
    selectPhaseItems,
    selectContents,
    selectMoments,
    selectPhases,
    selectServices,
  ],
  (phaseItems, contents, moments, phases, services) =>
    normalizePhaseItemsToApprovals({
      contents,
      moments,
      phaseItems,
      phases,
      services,
    })
)

export const selectNormalizedApprovals = createSelector(
  [selectNormalizedAssignments, selectNormalizedPhaseItems],
  (assignments, phaseItems) => {
    const approvals = [...phaseItems]
    if (testFeature('workflow_on_moments')) {
      approvals.push(...assignments)
    }
    return approvals
  }
)
